import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Dumbbell, 
  Apple, 
  Moon, 
  Brain, 
  ChevronRight,
  Users,
  BarChart3,
  BookOpen,
  Target,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Home = () => {
  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1761286753856-2f39b4413c1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx0ZW5uaXMlMjBwbGF5ZXIlMjBhY3Rpb24lMjBzaG90JTIwc3dpbmd8ZW58MHx8fHwxNzcwMDY4MjkzfDA&ixlib=rb-4.1.0&q=85"
            alt="Tennis player hitting a shot"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/50 to-[#0A0A0A]" />
        </div>

        {/* Content */}
        <div className="container-custom relative z-10 pt-32 pb-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-4xl"
          >
            <motion.p
              variants={fadeUp}
              className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-6"
            >
              Long-Term Athlete Development
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight uppercase leading-tight mb-8"
            >
              Building <span className="text-[#56C6C6]">Champions</span> From the Ground Up
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lg md:text-xl text-zinc-400 max-w-2xl mb-10 leading-relaxed"
            >
              We support young athletes with a structured, long-term approach to physical 
              preparation, recovery, and mindset development.
            </motion.p>
            <div className="flex flex-wrap gap-4">
              <Link to="/programs">
                <Button
                  data-testid="hero-cta-primary"
                  className="bg-[#56C6C6] text-black hover:bg-[#45B5B5] font-bold uppercase tracking-wider px-8 py-6 text-sm hover:shadow-[0_0_20px_rgba(86,198,198,0.4)] transition-all"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-[#56C6C6] rounded-full"
            />
          </div>
        </div>
      </section>

      {/* Four Pillars Section */}
      <section data-testid="pillars-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="mb-16">
            <p className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-4">
              Our Philosophy
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight uppercase">
              The Four Pillars
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Training */}
            <div
              data-testid="pillar-training"
              className="group relative h-[400px] overflow-hidden bg-[#121212] border border-white/5 hover:border-[#56C6C6]/30 transition-all duration-500"
            >
              <div className="absolute inset-0">
                <img
                  src="https://images.unsplash.com/photo-1726195221528-fe5c989b4512?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHx5b3VuZyUyMGF0aGxldGUlMjBzcHJpbnRpbmclMjB0cmFjayUyMGRhcmslMjBtb2RlJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwMDY2MDIzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Training"
                  className="w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
              </div>
              <div className="relative h-full flex flex-col justify-end p-6 z-10">
                <div className="mb-4">
                  <Dumbbell className="w-8 h-8 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-3">Training</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Developing movement quality, strength, speed, and physical resilience to support sport performance.
                </p>
              </div>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#56C6C6] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </div>

            {/* Nutrition */}
            <div
              data-testid="pillar-nutrition"
              className="group relative h-[400px] overflow-hidden bg-[#121212] border border-white/5 hover:border-[#56C6C6]/30 transition-all duration-500"
            >
              <div className="absolute inset-0">
                <img
                  src="https://images.unsplash.com/photo-1636044992119-be0892f9de94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwbWVhbCUyMHByZXAlMjBhdGhsZXRlJTIwbnV0cml0aW9ufGVufDB8fHx8MTc3MDA2NjAyN3ww&ixlib=rb-4.1.0&q=85"
                  alt="Nutrition"
                  className="w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
              </div>
              <div className="relative h-full flex flex-col justify-end p-6 z-10">
                <div className="mb-4">
                  <Apple className="w-8 h-8 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-3">Nutrition</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Building age-appropriate habits that support growth, energy availability, and recovery.
                </p>
              </div>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#56C6C6] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </div>

            {/* Recovery */}
            <div
              data-testid="pillar-recovery"
              className="group relative h-[400px] overflow-hidden bg-[#121212] border border-white/5 hover:border-[#56C6C6]/30 transition-all duration-500"
            >
              <div className="absolute inset-0">
                <img
                  src="https://images.unsplash.com/photo-1630225760711-ac8eaa0c8947?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxhdGhsZXRlJTIwc3RyZXRjaGluZyUyMGZvYW0lMjByb2xsZXIlMjByZWNvdmVyeXxlbnwwfHx8fDE3NzAwNjYwMzJ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Recovery"
                  className="w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
              </div>
              <div className="relative h-full flex flex-col justify-end p-6 z-10">
                <div className="mb-4">
                  <Moon className="w-8 h-8 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-3">Recovery</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Teaching athletes how sleep, rest, and load management protect health and performance.
                </p>
              </div>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#56C6C6] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </div>

            {/* Mindset */}
            <div
              data-testid="pillar-mindset"
              className="group relative h-[400px] overflow-hidden bg-[#121212] border border-white/5 hover:border-[#56C6C6]/30 transition-all duration-500"
            >
              <div className="absolute inset-0">
                <img
                  src="https://images.unsplash.com/photo-1654000748836-686dce1c1a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxmb2N1c2VkJTIweW91bmclMjBhdGhsZXRlJTIwY2xvc2UlMjB1cCUyMG1pbmRzZXR8ZW58MHx8fHwxNzcwMDY2MDM2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Mindset"
                  className="w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
              </div>
              <div className="relative h-full flex flex-col justify-end p-6 z-10">
                <div className="mb-4">
                  <Brain className="w-8 h-8 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-3">Mindset</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Developing confidence, focus, emotional control, and healthy attitudes toward training and competition.
                </p>
              </div>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#56C6C6] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </div>
          </div>
        </div>
      </section>

      {/* What V1 Provides Section */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-[#121212]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-4">
              What We Offer
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight uppercase mb-6">
              V1 Provides
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed mb-10">
              Our comprehensive approach ensures every athlete receives the guidance and 
              support they need to reach their full potential.
            </p>

            <div className="space-y-6">
              <div data-testid="service-0" className="flex items-start gap-4 p-4 bg-[#0A0A0A]/50 border border-white/5 hover:border-[#56C6C6]/20 transition-colors">
                <div className="w-12 h-12 bg-[#56C6C6]/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-white font-bold uppercase tracking-wide mb-1">Centralised Programming</h4>
                  <p className="text-zinc-400 text-sm">Physical preparation guidance for all athletes</p>
                </div>
              </div>
              <div data-testid="service-1" className="flex items-start gap-4 p-4 bg-[#0A0A0A]/50 border border-white/5 hover:border-[#56C6C6]/20 transition-colors">
                <div className="w-12 h-12 bg-[#56C6C6]/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-white font-bold uppercase tracking-wide mb-1">Development Framework</h4>
                  <p className="text-zinc-400 text-sm">Consistent athletic development aligned with each sport's pathway</p>
                </div>
              </div>
              <div data-testid="service-2" className="flex items-start gap-4 p-4 bg-[#0A0A0A]/50 border border-white/5 hover:border-[#56C6C6]/20 transition-colors">
                <div className="w-12 h-12 bg-[#56C6C6]/10 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-white font-bold uppercase tracking-wide mb-1">Education</h4>
                  <p className="text-zinc-400 text-sm">For athletes and parents across all four pillars</p>
                </div>
              </div>
              <div data-testid="service-3" className="flex items-start gap-4 p-4 bg-[#0A0A0A]/50 border border-white/5 hover:border-[#56C6C6]/20 transition-colors">
                <div className="w-12 h-12 bg-[#56C6C6]/10 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-[#56C6C6]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-white font-bold uppercase tracking-wide mb-1">Advanced Analytics</h4>
                  <p className="text-zinc-400 text-sm">Data and reporting across development, recovery, and readiness</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight uppercase mb-6">
              Ready to <span className="text-[#56C6C6]">Elevate</span> Your Game?
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Join the V1 Athletics community and start your journey towards athletic excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="cta-contact-btn"
                  className="bg-[#56C6C6] text-black hover:bg-[#45B5B5] font-bold uppercase tracking-wider px-8 py-6 text-sm w-full sm:w-auto"
                >
                  Contact Us
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
